/* eslint-disable camelcase */
import { faSpinner, faStoreAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DialogContent, Grid, TextField, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormElement, SelectField, SelectItemWithLeftElement } from 'components';
import { ItemOption } from 'components/_commons/Form';
import { AdornmentLoader } from 'components/forms/inputs/InputField/InputField';
import { IdentifierView } from 'models/IdentifierView';
import { LegalStatusView } from 'models/LegalStatusView';
import { useSnackbar, SnackbarMessage } from 'notistack';
import PropTypes from 'prop-types';
import React, {
 useCallback, useEffect, useState, ReactNode
} from 'react';
import { CompanyService, CountryService, LegalStatusService } from 'services';
import { i18nStore } from 'stores';
import { checkRegexMismatch, translate } from 'utils';
import { STRUCTURE_TYPE } from 'utils/constants';
import { CustomActionButtons } from './_CustomActionButtons';
import { HeaderModal } from './_HeaderModal';

type TranslateResult = string;

// save mui v4 styles
const StyledTextField = styled(TextField)({
    '& .MuiFormHelperText-root': {
        marginLeft: 0,
        marginRight: 0
    },
    '& .MuiFormHelperText-root.Mui-error': {
        color: '#f44336'
    },
    '& label.Mui-error': {
        color: '#f44336'
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: '#f44336'
    }
});


interface CreateCompanyModalProps {
    closeOnSubmit?: boolean;
    defaultValues: any;
    onClose: () => void;
    onConfirm: (data: any) => void;
    withDatabilanAcc?: boolean;
}

const LegalStatusValue = ({ data, innerProps }: { data: any; innerProps: any }) => (
  <ItemOption {...innerProps} style={{ gridArea: '1/1/2/3' }}>
    <Typography style={{ marginLeft: '0.8rem' }}>
      {data.name ?? null}
    </Typography>
  </ItemOption>
);

const LegalStatusOption = ({ data, innerProps }: { data: any; innerProps: any }) => (
  <ItemOption {...innerProps} isInsideSelect noPadding>
    <Typography style={{ marginLeft: '0.8rem' }}>
      {data.name}
    </Typography>
  </ItemOption>
);

export const CreateCompanyModal = ({
                                       onConfirm, onClose, closeOnSubmit, defaultValues, withDatabilanAcc = true
                                   }: CreateCompanyModalProps) => {
    const { currentLanguage } = i18nStore;
    const { enqueueSnackbar } = useSnackbar();
    const [isSearchingSiren, setIsSearchingSiren] = useState(false);

    const [values, setValues] = useState({
        ...defaultValues,
        country: defaultValues.country ?? { value: 0, countryCode: 'fr', label: 'France' },
        legalStatus: defaultValues.legalStatus ? defaultValues.legalStatus : null
    });
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

    const setField = useCallback((name: string, value: any) => {
        setValues(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const setTouched = useCallback((field: string) => {
        setTouchedFields(prevState => ({
            ...prevState,
            [field]: true
        }));
    }, []);

    const handleBlur = (field: string) => {
        setTouched(field);
    };

    const text = useCallback((name: string) => ({
        name,
        value: values[name] || '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setField(name, e.target.value),
        onBlur: () => {
            if (!values[name] && name === 'name') {
                setErrors(prev => ({ ...prev, [name]: true }));
            } else {
                setErrors(prev => ({ ...prev, [name]: false }));
            }
        }
    }), [values, setField]);

    const raw = useCallback((name: string) => ({
        name,
        value: values[name],
        onChange: (value: any) => setField(name, value)
    }), [values, setField]);

    const number = useCallback((options: { name: string, onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void }) => ({
        name: options.name,
        value: values[options.name] || '',
        onChange: options.onChange || ((e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value === '' ? '' : Number(e.target.value);
            setField(options.name, val);
        })
    }), [values, setField]);

    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingCountries, setIsFetchingCountries] = useState(false);
    const [countriesSelectItems, setCountriesSelectItems] = useState([]);
    // The current values for the identifier fields
    const [identifierFields, setIdentifierFields] = useState<IdentifierView[]>([]);
    // The available identifier fields for the selected country
    const [availableFields, setAvailableFields] = useState<IdentifierView[]>([]);
    const [legalStatuses, setLegalStatuses] = useState<LegalStatusView[]>([]);

    const updateIdentifiers = useCallback((newFields: IdentifierView[]) => {
        // Replace the existing fields with the new ones
        setIdentifierFields((value) => availableFields.map(field => {
            const newField = newFields?.find(f => f.idField === field.idField);
            if (newField) {
                const { idField } = newField;
                setTouchedFields({
                    ...touchedFields,
                    [idField]: false
                });
                return newField;
            }
            const existingField = value?.find(f => f.idField === field.idField);
            if (existingField) {
                return existingField;
            }
            return field;
        }));
    }, [availableFields, touchedFields]);

    const generateTVAFromSirenIfEmpty = useCallback((sirenField) => {
        let tvaIntraField = availableFields?.find((idField) => idField.name === 'TVA intracommunautaire');
        const existingTvaIntra = values.identifiers?.find((idField: any) => idField.idField === tvaIntraField?.idField);
        if (tvaIntraField && (!existingTvaIntra || existingTvaIntra.value === null)) {
            let tvaCode = `${(12 + 3 * (Number(sirenField.value) % 97)) % 97}`;
            if (tvaCode.length === 1) {
                tvaCode = `0${tvaCode}`;
            }
            const tvaIntraValue = `FR ${tvaCode}${sirenField.value}`;
            tvaIntraField = {
                ...tvaIntraField,
                value: tvaIntraValue
            };
            updateIdentifiers([sirenField, tvaIntraField]);
        } else if (existingTvaIntra?.value) {
            tvaIntraField = {
                ...tvaIntraField,
                value: existingTvaIntra?.value
            };
            updateIdentifiers([sirenField, tvaIntraField]);
        } else {
            updateIdentifiers([sirenField]);
        }
    }, [values.identifiers, availableFields, updateIdentifiers]);

    const getCompanyBySiren = useCallback((sirenField: IdentifierView) => {
        generateTVAFromSirenIfEmpty(sirenField);
        setIsSearchingSiren(true);
        CompanyService.getCompanyBySiren(sirenField.value).then(response => {
            if (response.header.statut === 200) {
                const tempCompany = response.uniteLegale.periodesUniteLegale[0].denominationUniteLegale;
                const tempLegalStatus = legalStatuses?.find(stat => stat.name === response.uniteLegale.periodesUniteLegale[0].categorieJuridiqueUniteLegale);
                if (!values.name) {
                    setField('name', tempCompany);
                }
                if (!values.legalStatus) {
                    tempLegalStatus && setField('legalStatus', tempLegalStatus);
                }
            } else {
                enqueueSnackbar(response.header.message, { variant: 'warning', autoHideDuration: 5000 });
            }
        }).catch(() => {
            enqueueSnackbar(translate('errors.noMatchingSiren') as SnackbarMessage , { variant: 'warning', autoHideDuration: 5000 });
        }).finally(() => setIsSearchingSiren(false));
        // eslint-disable-next-line
    }, [enqueueSnackbar, values, availableFields, legalStatuses, updateIdentifiers, identifierFields, generateTVAFromSirenIfEmpty, setField]);

    const handleIdentifierValueChange = useCallback((identifierField: IdentifierView) => {
        if (identifierField.primary && !checkRegexMismatch(identifierField.rule?.format, identifierField.value)
            && values.country
            && values.country.countryCode === 'fr'
        ) {
            getCompanyBySiren(identifierField);
        } else {
            updateIdentifiers([identifierField]);
        }
    }, [values.country, getCompanyBySiren, updateIdentifiers]);

    useEffect(() => {
        // When the available identifier fields change, initialise them with the current data
        availableFields.forEach(idFieldToInit => {
            const existingField = values.identifiers?.find((idField: any) => idField.idField === idFieldToInit.idField);
            // If the field is already defined with a value, reload it to update other fields
            if (existingField && existingField.value) {
                handleIdentifierValueChange(existingField);
            } else {
                handleIdentifierValueChange(idFieldToInit);
            }
        });
        // eslint-disable-next-line
    }, [availableFields]);

    const fetchLegalStatusList = useCallback(() => {
        LegalStatusService.getLegalStatusByCountryId(values.country.value)
            // eslint-disable-next-line no-nested-ternary
            .then(resp => setLegalStatuses(resp.sort((a, b) => a.name?.localeCompare(b.name))));
    }, [setLegalStatuses, values.country.value]);

    const handleChangeSelectedCountry = useCallback(countryItem => {
        setField('country', countryItem);
        CountryService.getCountryById(countryItem.id)
            .then(() => setIdentifierFields([]))
            .then(() => setField('legalStatus', null));
    }, [setField]);

    useEffect(() => {
        setIsFetchingCountries(true);
        CountryService.getCountrySelectItems()
            .then(json => {
                setCountriesSelectItems(json);
            })
            .finally(() => setIsFetchingCountries(false));
    }, []);

    useEffect(() => {
        setIsLoading(true);
        CountryService.getCountryById(values.country.value)
            .then((country) => {
                fetchLegalStatusList();
                const companyIdentifierFields = country.identifiers.filter((identifierField: any) => identifierField.type === STRUCTURE_TYPE.COMPANY);
                setAvailableFields(companyIdentifierFields);
            }).finally(() => {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        });
        // eslint-disable-next-line
    }, [defaultValues, values.country]);

    const handleValidateModal = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        // Correction pour document.forms.companyForm
        const form = document.querySelector('form[name="companyForm"]') as HTMLFormElement;
        const isFormValid = form && form.reportValidity();

        if (isFormValid) {
            onConfirm({
                ...values,
                identifiers: identifierFields,
                id: defaultValues.id || null
            });

            if (closeOnSubmit) {
                onClose();
            }
        }
    }, [onConfirm, values, identifierFields, defaultValues.id, closeOnSubmit, onClose]);

    const handleChangeAccountLimit = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { target: { value } } = e;
        const limit = Number(value);

        if (limit < 1) {
            e.preventDefault();
            return;
        }

        setField('databilanAccountLimit', limit);
    }, [setField]);

    const customFilter = useCallback((legalStatus, search) => {
        if (search) {
            return (legalStatus.data.name).toUpperCase().includes(search.toUpperCase());
        }
        return true;
    }, []);

    return (
      <form autoComplete="off" name="companyForm" onSubmit={handleValidateModal}>
        <HeaderModal onClose={onClose}>
          <FontAwesomeIcon icon={faStoreAlt} />
          <Typography component="span">
            {defaultValues && defaultValues.name
                        ? (translate('modalCompany.editCompany') as TranslateResult)
                        : (translate('modalCompany.newCompany') as TranslateResult)}
          </Typography>
        </HeaderModal>

        <DialogContent style={{ width: '500px', paddingTop: '10px' }}>
          <Grid container direction="column">
            <FormElement label={translate('common.country') as TranslateResult}>
              <SelectItemWithLeftElement
                            isFlagElement
                            isLoading={isFetchingCountries}
                            label="common.selectCountry"
                            name="countries"
                            options={countriesSelectItems}
                            onChange={handleChangeSelectedCountry}
                            {...raw('country')}
                        />
            </FormElement>
            <FormElement label={translate('common.company') as TranslateResult}>
              {isLoading ? (
                <Grid item>
                  <div>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    {translate('common.loading') as TranslateResult}
                  </div>
                </Grid>
                            )
                            : (identifierFields.map(identifierField => {
                                const {
                                    idField, value, name, required, rule
                                } = identifierField;
                                return (
                                  <StyledTextField
                                        error={(touchedFields[idField] && !value) || (value && checkRegexMismatch(rule?.format, value))}
                                        helperText={(rule?.format && rule?.translations?.find(val => val.code === currentLanguage)?.label) ?? ''}
                                        inputProps={rule?.format ? { pattern: rule.format } : {}}
                                        key={idField}
                                        label={name as ReactNode}
                                        name={name}
                                        required={required}
                                        value={value ?? ''}
                                        onBlur={() => handleBlur(idField)}
                                        onChange={val => handleIdentifierValueChange({ ...identifierField, value: val.target.value })}
                                    />
                                );
                            }))}
              <StyledTextField
                            error={!!errors.name}
                            InputProps={{
                                endAdornment: (isSearchingSiren && <AdornmentLoader />)
                            }}
                            label={translate('common.corporateName') as ReactNode}
                            name="name"
                            required
                            {...text('name')}
                        />
              {withDatabilanAcc && (
                <StyledTextField
                                error={!!errors.databilanAccountLimit}
                                inputProps={{
                                    min: 0,
                                    type: 'number',
                                    step: 1
                                }}
                                label={translate('common.databilanAccountLimit') as ReactNode}
                                name="databilanAccountLimit"
                                {...number({
                                    name: 'databilanAccountLimit',
                                    onChange: handleChangeAccountLimit
                                })}
                            />
                        )}
            </FormElement>

            <div style={{ margin: '1rem 0' }} />

            <SelectField
                        components={{ Option: LegalStatusOption, SingleValue: LegalStatusValue }}
                        filterOption={customFilter}
                        label="common.legalStatus"
                        menuPlacement="top"
                        name="legalStatus"
                        options={legalStatuses ?? []}
                        required
                        {...raw('legalStatus')}
                    />
          </Grid>
        </DialogContent>

        <CustomActionButtons
                isValidated={defaultValues.validated}
                onClose={onClose}
            />
      </form>
    );
};

CreateCompanyModal.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    closeOnSubmit: PropTypes.bool,
    defaultValues: PropTypes.shape({}),
    withDatabilanAcc: PropTypes.bool
};

CreateCompanyModal.defaultProps = {
    closeOnSubmit: true,
    defaultValues: {},
    withDatabilanAcc: true
};
